import * as S from './styles'
import * as Yup from 'yup'

import { Button, Input, Select } from '@monorepo/components'
import { Field, Form, Formik, setNestedObjectValues } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'


import {
  httpUserInfo,
  httpDialogDirect,
  ChannelHelper,
  KeycloakHelper,
} from '@monorepo/infra'
import { useHistory, useLocation } from 'react-router-dom'
import { useKeycloak } from '@react-keycloak/web'
import { DialogDirectAddCustomerRequest } from '@monorepo/interfaces'

interface MasterDealer {
  _id: string
  channels: string[]
  billingAddresses?: {
    address: string
    city: string
    state: string
    zipCode: string
    default: boolean
  }
  shippingAddresses?: {
    address: string
    city: string
    state: string
    zipCode: string
    default: boolean
  }
  corpId: string
  masterDealerId: string
  contractId: string
  name: string
  __v: number
}

interface AssociatedMasterDealer {
  masterDealerId: string
  masterDealer: MasterDealer[]
  channels: string[]
  dealerTypes: string[]
  corpId: string
}

interface Dealer {
  _id: string
  associatedMasterDealers: AssociatedMasterDealer[]
  userId: string[]
  __v: number
}

export interface RegistrationUserResponse {
  _id: string
  keycloakUserId: string
  groups: string[]
  phoneNumber?: string
  email: string
  contactEmail: string
  contactPhone: string
  preferredContactMethod: string
  lastName: string
  firstName: string
  __v: number
  dealer: Dealer
}

type RegistrationUserPayload = {
  _id: string
  userId: string
  firstNAme: string
  lastName: string
  password: string
  email: string
  associatedMasterDealers: {
    masterDealerId: string
  }[]
}

interface RegistrationUserForm {
  contactEmail: string
  contactPhone: string
  lastName: string
  password: string
  firstName: string
}

interface ValidateEmailForm {
  email: string
}

interface EncryptType {
  salt: string
  iv: string
  tag: string
  encrypted: string
}

interface DecodeType extends EncryptType {
  sc: string
}

const ValidateEmailSchema = Yup.object().shape({
  email: Yup.string()
    .required('Please enter your e-mail')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i, 'Invalid email.'),
})

const RegistrationUserSchema = Yup.object().shape({
  firstName: Yup.string().required('Please enter your first name.'),
  lastName: Yup.string().required('Please enter your last name.'),
  password: Yup.string()
    .required('Please enter your password.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character'
    ),
  contactEmail: Yup.string()
    .required('Please enter a preferred email address.')
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i,
      'Invalid email address.'
    ),
  contactPhone: Yup.string()
    .required('Please enter a phone number.')
    .matches(
      /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/i,
      'Invalid phone number.'
    ),
})

const REGISTRATION_STEPS = {
  EMAIL: 'EMAIL',
  REGISTER: 'REGISTER',
}

const Registration: React.FC = () => {
  const [registrationStep, setRegistrationStep] = useState(
    REGISTRATION_STEPS.EMAIL
  )
  const [emailErrorMessage, setEmailErrorMessage] = useState('')

  const [registrationUserPayload, setRegistrationUserPayload] =
    useState<Partial<RegistrationUserPayload> | null>(null)

  const [dialogDirectAddCustomerPayload, setDialogDirectAddCustomerPayload] =
    useState<Partial<DialogDirectAddCustomerRequest> | null>(null)

  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(
    undefined
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [passwordValue, setPasswordValue] = useState('')
  const [passwordValidations, setPasswordValidations] = useState({
    hasMinLength: false,
    hasNumberOrSymbol: false,
    hasMixedCase: false,
    isStrong: false,
  })
  const [showPassword, setShowPassword] = useState(false)


  const { initialized, keycloak } = useKeycloak()
  const history = useHistory()
  const location = useLocation()
  const [isAutoLoading, setIsAutoLoading] = useState(false)

  const user = KeycloakHelper.getTokenParsed(keycloak)
  const validatePassword = (password: string) => {
    const hasMinLength = password.length >= 8
    const hasNumberOrSymbol = /[0-9!@#$%^&*(),.?":{}|<>]/.test(password)
    const hasMixedCase = /[a-z]/.test(password) && /[A-Z]/.test(password)
    const isStrong = hasMinLength && hasNumberOrSymbol && hasMixedCase
    setPasswordValidations({
      hasMinLength,
      hasNumberOrSymbol,
      hasMixedCase,
      isStrong,
    })
  }


  const decodeContent = async (input: DecodeType) => {
    const { salt, iv, tag, encrypted, sc } = input
    function hexToBuffer(hex: string): Uint8Array {
      return new Uint8Array(
        hex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16))
      )
    }

    const enc = new TextEncoder()

    const saltBuf = hexToBuffer(salt)
    const ivBuf = hexToBuffer(iv)
    const tagBuf = hexToBuffer(tag)
    const encryptedBuf = hexToBuffer(encrypted)

    const ciphertextWithTag = new Uint8Array(
      encryptedBuf.length + tagBuf.length
    )
    ciphertextWithTag.set(encryptedBuf, 0)
    ciphertextWithTag.set(tagBuf, encryptedBuf.length) //

    const baseKey = await crypto.subtle.importKey(
      'raw',
      enc.encode(sc),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    )
    const aesKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltBuf,
        iterations: 100000,
        hash: 'SHA-256',
      },
      baseKey,
      {
        name: 'AES-GCM',
        length: 256,
      },
      false,
      ['decrypt']
    )
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBuf,
        tagLength: 128,
      },
      aesKey,
      ciphertextWithTag
    )
    const decoded = new TextDecoder().decode(decrypted)

    return decoded
  }

  const getUserInfoByEmail = useCallback((formValues: ValidateEmailForm) => {
    const { email } = formValues

    if (!email) return

    setIsSubmitting(true)

    httpUserInfo
      .get({ url: `/dealer/public/${email}` })
      .then(async (response) => {
        const sc =
          process.env.REACT_APP_SCT || '7ab9f43705927ea6c37732e20067f303!'
        const { data } = response

        const { salt, iv, tag, encrypted } = data as EncryptType

        const decoded = await decodeContent({
          salt,
          iv,
          tag,
          encrypted,
          sc,
        })

        const registrationUser = JSON.parse(decoded)

        const billingAddresses =
          registrationUser?.dealer?.associatedMasterDealers?.[0]
            ?.masterDealer?.[0]?.billingAddresses

        const channels = ChannelHelper.getChannels(
          registrationUser?.dealer?.associatedMasterDealers
        )

        if (billingAddresses != null || !channels.length) {
          setEmailErrorMessage('Error fetching information for this e-mail.')
          return
        }

        const { dealer } = registrationUser
        setRegistrationUserPayload({
          email: email.trim(),
        })

        setDialogDirectAddCustomerPayload({
          companyName: '',
          emailAddress: email.trim(),
          channel: channels?.[0]?.toUpperCase(),
          multiCorpId: channels
            .map((item) =>
              ChannelHelper.getMasterDealerId(
                dealer.associatedMasterDealers[0].masterDealerId,
                item.toUpperCase()
              )
            )
            .join('|'),
          corpId: ChannelHelper.getMasterDealerId(
            dealer.associatedMasterDealers[0].masterDealerId,
            channels?.[0]?.toUpperCase()
          ),
          address1: 'address1',
          addressee: '',
          city: 'Palo Alto',
          zip: '94301',
          state: 'CA',
          phoneNumber: '',
          attention: '',
        })

        setRegistrationStep(REGISTRATION_STEPS.REGISTER)
      })
      .catch((e) => {
        console.error(e)
        // Extract error message from API HTTPException response
        const errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Error fetching information for this e-mail.'
        setEmailErrorMessage(errorMessage)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }, [])

  const registerUser = useCallback(
    (formValues: RegistrationUserForm) => {
      const {
        password,
        firstName,
        lastName,
        contactEmail,
        contactPhone,
      } = formValues

      const registrationPayload = {
        ...registrationUserPayload,
        password,
        firstName,
        lastName,
        contactEmail,
        contactPhone,
      } as RegistrationUserPayload

      const dialogDirectAddCustomerRequest = {
        ...dialogDirectAddCustomerPayload,
        firstName,
        lastName,
      } as DialogDirectAddCustomerRequest

      setErrorMessage(undefined)
      setIsSubmitting(true)

      httpUserInfo
        .post({ url: `/registration`, data: registrationPayload })
        .then((response) => {
          httpDialogDirect
            .post({
              url: '/AddCustomer',
              data: dialogDirectAddCustomerRequest,
            })
            .then(() => {
              history.push('/')
            })
            .catch((error) => {
              console.error(error)
              setErrorMessage('AddCustomer error')
              setIsSubmitting(false)
            })
        })
        .catch((error) => {
          setErrorMessage('Registration error')
          setIsSubmitting(false)
        })
    },
    [history, registrationUserPayload, dialogDirectAddCustomerPayload]
  )

  const handleSubmit = useCallback(
    (values: ValidateEmailForm | RegistrationUserForm) => {
      if (registrationStep === REGISTRATION_STEPS.EMAIL) {
        getUserInfoByEmail(values as ValidateEmailForm)
        return
      }

      registerUser(values as RegistrationUserForm)
    },
    [getUserInfoByEmail, registerUser, registrationStep]
  )

  // Auto-skip EMAIL step when redirected from Keycloak with ?email param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const emailFromUrl = searchParams.get('email')

    if (emailFromUrl) {
      setIsAutoLoading(true)
      getUserInfoByEmail({ email: emailFromUrl })
    }
  }, [location.search, getUserInfoByEmail])

  // Clear auto-loading state once email API call succeeds and step advances
  useEffect(() => {
    if (registrationStep === REGISTRATION_STEPS.REGISTER) {
      setIsAutoLoading(false)
    }
  }, [registrationStep])

  // Clear auto-loading on error (fallback to manual EMAIL step)
  useEffect(() => {
    if (emailErrorMessage) {
      setIsAutoLoading(false)
    }
  }, [emailErrorMessage])

  useEffect(() => {
    if (initialized && user) {
      history.push('/')
    }
  }, [history, initialized, user])

  if (!initialized) return null

  return (
    <S.Wrapper>
      <S.LeftSide />

      <S.RightSide>
        <S.BrandHeader>
          <img src="/images/logo-header.svg" alt="DIRECTV" />
        </S.BrandHeader>

        <S.FormSection>
          <S.FormTitle>
            Register
          </S.FormTitle>
          <S.FormSubtitle>
            Please complete your registration to get started
          </S.FormSubtitle>
          <S.FormContainer>
            <Formik
              initialValues={
                registrationStep === REGISTRATION_STEPS.EMAIL
                  ? { email: '' }
                  : {
                    contactEmail: '',
                    contactPhone: '',
                    lastName: '',
                    password: '',
                    firstName: '',
                  }
              }
              validationSchema={
                registrationStep === REGISTRATION_STEPS.EMAIL
                  ? ValidateEmailSchema
                  : RegistrationUserSchema
              }
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values, actions) => {
                handleSubmit(values)
                actions.setSubmitting(false)
              }}
            >
              {({
                errors,
                touched,
                values,
                submitForm,
                setFieldValue,
                setTouched,
                setFieldTouched,
                validateForm,
              }) => (
                <Form>
                  <S.Wrap>
                    {registrationStep === REGISTRATION_STEPS.EMAIL ? (
                      isAutoLoading ? (
                        <S.Column style={{ textAlign: 'center', padding: '40px 0' }}>
                          <p>Verifying your email...</p>
                        </S.Column>
                      ) : (
                        <S.Column>
                          <Field
                            id="email"
                            name="email"
                            type="email"
                            component={Input}
                            invalid={
                              emailErrorMessage || (touched.email && errors.email)
                            }
                            invalidMessage={emailErrorMessage || errors.email}
                            label="E-mail"
                            onBlur={() => setFieldTouched('email')}
                            onChange={(option: { target: { value: string } }) => {
                              setFieldValue('email', option.target.value)
                            }}
                          />
                        </S.Column>
                      )
                    ) : (
                      <S.Column>
                        <S.Row>
                          <Field
                            id="firstName"
                            name="firstName"
                            component={Input}
                            invalid={touched.firstName && errors.firstName}
                            invalidMessage={errors.firstName}
                            label="First Name"
                            onBlur={() => setFieldTouched('firstName')}
                            onChange={(option: { target: { value: string } }) => {
                              setFieldValue('firstName', option.target.value)
                            }}
                          />

                          <Field
                            id="lastName"
                            name="lastName"
                            component={Input}
                            invalid={touched.lastName && errors.lastName}
                            invalidMessage={errors.lastName}
                            label="Last Name"
                            onBlur={() => {
                              setFieldTouched('lastName')
                            }}
                            onChange={(option: { target: { value: string } }) => {
                              setFieldValue('lastName', option.target.value)
                            }}
                          />
                        </S.Row>

                        <S.Column>
                          <S.PasswordWrapper>
                            <Field
                              id="password"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              component={Input}
                              label="Create Password"
                              onBlur={() => {
                                setFieldTouched('password')
                              }}
                              onChange={(option: { target: { value: string } }) => {
                                const newPassword = option.target.value
                                setFieldValue('password', newPassword)
                                setPasswordValue(newPassword)
                                validatePassword(newPassword)
                              }}
                            />
                            <S.PasswordToggle
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? 'Hide' : 'Show'}
                            </S.PasswordToggle>
                          </S.PasswordWrapper>
                          {
                            touched.password && errors.password && (
                              <S.PasswordValidationList>
                                <S.ValidationTitle>
                                  Password must meet the following requirements:
                                </S.ValidationTitle>
                                <S.ValidationItem met={passwordValidations.isStrong}>
                                  <S.ValidationIcon met={!passwordValidations.isStrong}>
                                    {passwordValidations.isStrong ? '✓' : '✕'}
                                  </S.ValidationIcon>
                                  Password strength: {passwordValidations.isStrong ? 'strong' : 'weak'}
                                </S.ValidationItem>
                                <S.ValidationItem met={passwordValidations.hasMinLength}>
                                  <S.ValidationIcon met={passwordValidations.hasMinLength}>
                                    {passwordValidations.hasMinLength ? '✓' : '✕'}
                                  </S.ValidationIcon>
                                  At least 8 characters
                                </S.ValidationItem>
                                <S.ValidationItem met={passwordValidations.hasNumberOrSymbol}>
                                  <S.ValidationIcon met={passwordValidations.hasNumberOrSymbol}>
                                    {passwordValidations.hasNumberOrSymbol ? '✓' : '✕'}
                                  </S.ValidationIcon>
                                  Contains a number or symbol
                                </S.ValidationItem>
                                <S.ValidationItem met={passwordValidations.hasMixedCase}>
                                  <S.ValidationIcon met={passwordValidations.hasMixedCase}>
                                    {passwordValidations.hasMixedCase ? '✓' : '✕'}
                                  </S.ValidationIcon>
                                  At least one lower and upper case
                                </S.ValidationItem>
                              </S.PasswordValidationList>
                            )}


                          <Field
                            id="contactEmail"
                            name="contactEmail"
                            component={Input}
                            invalid={touched.contactEmail && errors.contactEmail}
                            invalidMessage={errors.contactEmail}
                            label="Preferred Email Address"
                            onBlur={() => {
                              setFieldTouched('contactEmail')
                            }}
                            onChange={(option: { target: { value: string } }) => {
                              setFieldValue('contactEmail', option.target.value)
                            }}
                          />

                          <Field
                            id="contactPhone"
                            name="contactPhone"
                            component={Input}
                            invalid={touched.contactPhone && errors.contactPhone}
                            invalidMessage={errors.contactPhone}
                            label="Phone Number"
                            onBlur={() => {
                              setFieldTouched('contactPhone')
                            }}
                            onChange={(option: { target: { value: string } }) => {
                              setFieldValue('contactPhone', option.target.value)
                            }}
                          />
                        </S.Column>
                      </S.Column>
                    )}
                  </S.Wrap>
                  <button
                    style={{
                      backgroundColor: isSubmitting ? 'grey' : 'black',
                      width: '100%',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      letterSpacing: '0.5px',
                    }}
                    onClick={() => {
                      validateForm().then((errors) => {
                        setTouched(setNestedObjectValues(errors, true))
                      })

                      submitForm()
                    }}
                    disabled={isSubmitting}
                  >
                    {registrationStep === REGISTRATION_STEPS.EMAIL
                      ? 'VERIFY YOUR E-MAIL'
                      : 'REGISTER'}
                  </button>
                  {!!errorMessage ? (
                    <S.ErrorContainer>
                      <S.ErrorMessage>{errorMessage}</S.ErrorMessage>
                    </S.ErrorContainer>
                  ) : undefined}

                  {/* {registrationStep === REGISTRATION_STEPS.EMAIL && (
                    <S.HelpSection>
                      <S.HelpText>
                        Need help signing in?
                        <br />
                        <S.TextLink href="mailto:zoners@thedirectvmarketingzone.com">
                          Send a message to zoners@thedirectvmarketingzone.com
                        </S.TextLink>
                      </S.HelpText>
                    </S.HelpSection>
                  )} */}
                </Form>
              )}
            </Formik>
          </S.FormContainer>
        </S.FormSection>
        <S.FooterSection>
          <S.FooterText>
            ©{new Date().getFullYear()} DIRECTV. DIRECTV and all other DIRECTV
            marks are trademarks of DIRECTV, LLC.
            <br />
            All other marks are the property of their respective owners.
          </S.FooterText>
        </S.FooterSection>
      </S.RightSide>
    </S.Wrapper>
  )
}

export default Registration
